<template>
  <section
    id="services"
    class="services"
  >
    <img
      class="services__stars"
      src="/assets/pictos/decor-services.png"
      alt=""
      aria-hidden="true"
    >
    <div class="services__intro">
      <div class="services__photo">
        <img
          :src="data.services.image"
          alt=""
        >
      </div>
      <div class="services__text">
        <p class="section-kicker">{{ data.services.kicker }}</p>
        <h2 class="section-title">{{ data.services.title }}</h2>
        <p
          v-for="(p, i) in bodyParagraphs"
          :key="i"
          class="services__paragraph"
          v-html="p"
        />
      </div>
    </div>

    <div class="services__pills">
      <button
        v-for="cat in data.services.categories"
        :key="cat.id"
        class="services__pill"
        :class="{ 'is-active': cat.id === activeCategoryId }"
        @click="selectCategory(cat.id)"
      >
        <img
          class="services__pill-icon"
          :src="cat.icon"
          alt=""
        >
        {{ cat.label }}
      </button>
    </div>

    <!-- carrousel interactif (cf. animations PDF, section 3) -->
    <div
      class="services__carousel"
      @mouseenter="stopAutoplay"
      @mouseleave="startAutoplay"
    >
      <button
        class="services__arrow is-prev"
        aria-label="Précédent"
        @click="step(-1); restartAutoplay()"
      >←</button>

      <div class="services__stage">
        <div
          v-for="(item, i) in activeItems"
          :key="`${activeCategoryId}-${i}`"
          class="services__slide"
          :class="slideClass(i)"
          @click="goTo(i)"
        >
          <div class="services__slide-media">
            <img
              v-if="item.image"
              :src="item.image"
              :alt="item.caption"
            >
            <div
              v-else
              class="services__placeholder"
            >
              <img
                src="/assets/pictos/etoile_cta.svg"
                alt=""
              >
              <span>Visuel à venir</span>
            </div>
          </div>
        </div>
      </div>

      <button
        class="services__arrow is-next"
        aria-label="Suivant"
        @click="step(1); restartAutoplay()"
      >→</button>

      <p class="services__caption-overlay">{{ activeCaption }}</p>
    </div>
    <p class="services__caption">{{ activeCaption }}</p>
  </section>
</template>

<script>
import { toParagraphs } from '@/content/richtext'

export default {
  name: 'ServicesSection',
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      activeCategoryId: this.data.services.categories[0]?.id,
      slideIndex: 1,
      autoplayId: null
    }
  },
  mounted() {
    this.startAutoplay()
  },
  beforeUnmount() {
    this.stopAutoplay()
  },
  computed: {
    bodyParagraphs() {
      return toParagraphs(this.data.services.body)
    },
    activeCategory() {
      return (
        this.data.services.categories.find((c) => c.id === this.activeCategoryId) ||
        this.data.services.categories[0]
      )
    },
    activeItems() {
      return this.activeCategory.items
    },
    activeCaption() {
      return this.activeItems[this.slideIndex]?.caption || ''
    }
  },
  methods: {
    // défilement automatique (désactivé si l'utilisateur préfère moins d'animations)
    startAutoplay() {
      this.stopAutoplay()
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
      this.autoplayId = window.setInterval(() => this.step(1), 2000)
    },
    stopAutoplay() {
      if (this.autoplayId) {
        window.clearInterval(this.autoplayId)
        this.autoplayId = null
      }
    },
    // une interaction reprend la main : on repart d'un cycle complet
    restartAutoplay() {
      this.startAutoplay()
    },
    selectCategory(id) {
      this.activeCategoryId = id
      this.slideIndex = Math.min(1, this.activeItems.length - 1)
      this.restartAutoplay()
    },
    step(dir) {
      const n = this.activeItems.length
      this.slideIndex = (this.slideIndex + dir + n) % n
    },
    goTo(i) {
      this.slideIndex = i
      this.restartAutoplay()
    },
    slideClass(i) {
      const n = this.activeItems.length
      if (i === this.slideIndex) return 'is-current'
      if ((this.slideIndex - 1 + n) % n === i) return 'is-prev'
      if ((this.slideIndex + 1) % n === i) return 'is-next'
      return 'is-hidden'
    }
  }
}
</script>

<style lang="scss" scoped>
.services {
  background: $navy;
  color: $white;
  padding-top: 0;
  // au-dessus du plan sticky de la section précédente qui déborde vers le bas
  position: relative;
  z-index: 1;
}

// décor : collé au coin haut-droit du viewport, grand écran uniquement.
// Positionné sur la section (et non sur le container centré) : right: 0 = bord
// du viewport, sans calcul de marge.
.services__stars {
  display: none;
  position: absolute;
  top: 0;
  right: 0;
  width: clamp(110px, 10vw, 150px);
  pointer-events: none;
  user-select: none;

  @media (min-width: 1400px) {
    display: block;
  }
}

.services__intro .section-title {
  white-space: pre-line;
}

.services__intro {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: clamp(28px, 5vw, 90px);
  align-items: stretch;
  width: min(1280px, 100%);
  margin: 0 auto;
  padding: 84px 22px 56px;
}

// la photo reste dans le container, alignée verticalement avec le bloc texte
.services__photo {
  position: relative;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 6px;
  }
}

.services__paragraph {
  margin-top: 18px;
  font-size: 15px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.92);

  :deep(strong) {
    font-weight: 800;
    color: $white;
  }
}

.services__pills {
  width: min(1320px, calc(100% - 44px));
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: clamp(12px, 1.6vw, 22px);
  padding-bottom: 64px;
}

.services__pill {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  max-width: 250px;
  padding: 11px 18px;
  border-radius: 999px;
  border: 1.5px solid $yellow;
  background: none;
  color: $yellow;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-align: left;
  line-height: 1.25;
  transition: all 0.22s ease;

  &-icon {
    width: 24px;
    height: 24px;
  }

  &.is-active {
    background: $red;
    border-color: $red;
    color: $white;

    // picto livré en jaune -> blanc quand sélectionné
    .services__pill-icon {
      filter: brightness(0) invert(1);
    }
  }

  &:hover:not(.is-active) {
    background: rgba($yellow, 0.12);
  }
}

// ---- carrousel ----
.services__carousel {
  position: relative;
  background: $beige;
  padding: 56px 0 24px;
  display: flex;
  align-items: center;
}

.services__stage {
  position: relative;
  width: 100%;
  height: clamp(300px, 42vw, 520px);
  overflow: hidden;
}

.services__slide {
  position: absolute;
  top: 0;
  bottom: 0;
  width: min(640px, 58%);
  left: 50%;
  display: flex;
  align-items: center;
  transition: transform 0.45s ease, opacity 0.45s ease;
  cursor: pointer;

  &-media {
    width: 100%;
    height: 100%;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &.is-current {
    transform: translateX(-50%);
    z-index: 3;
  }

  &.is-prev {
    transform: translateX(-152%) scale(0.82);
    z-index: 2;
    opacity: 0.8;
  }

  &.is-next {
    transform: translateX(52%) scale(0.82);
    z-index: 2;
    opacity: 0.8;
  }

  &.is-hidden {
    opacity: 0;
    transform: translateX(-50%) scale(0.85);
    pointer-events: none;
  }
}

.services__placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  background: repeating-linear-gradient(
    -45deg,
    darken($beige, 6%),
    darken($beige, 6%) 14px,
    darken($beige, 9%) 14px,
    darken($beige, 9%) 28px
  );
  color: $navy;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;

  img {
    width: 34px;
    opacity: 0.7;
  }
}

.services__arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 4;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: $navy;
  color: $white;
  font-size: 15px;
  transition: background 0.2s ease;

  &:hover {
    background: $red;
  }

  &.is-prev { left: 34px; }
  &.is-next { right: 34px; }
}

.services__caption {
  background: $beige;
  color: $navy;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  padding: 0 22px 44px;
  min-height: 22px;
}

// légende dans l'image, mobile uniquement
.services__caption-overlay {
  display: none;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 4;
  padding: 44px 60px 16px;
  text-align: center;
  color: $white;
  font-size: 14px;
  font-weight: 700;
  background: linear-gradient(180deg, transparent, rgba(0, 0, 0, 0.6));
  pointer-events: none;
}

@include mobile {
  .services__intro {
    grid-template-columns: 1fr;
    padding: 60px 22px 36px;
  }

  .services__photo {
    margin: -60px -22px 0;

    img {
      position: static;
      height: auto;
      max-height: 340px;
      width: 100%;
      object-fit: cover;
    }
  }

  .services__pills {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    padding-inline: 14px;
  }

  .services__pill {
    min-width: 0;
    max-width: none;
    justify-content: center;
    font-size: 10px;
    letter-spacing: 0.02em;
    padding: 12px 8px;

    &-icon {
      width: 20px;
      height: 20px;
    }
  }

  // slide plein écran, légende dans l'image, flèches par-dessus
  .services__carousel {
    padding: 0;
  }

  .services__stage {
    height: 72vw;
  }

  .services__slide {
    width: 100%;

    &.is-prev { transform: translateX(-155%); }
    &.is-next { transform: translateX(55%); }
  }

  .services__arrow {
    width: 40px;
    height: 40px;
    background: rgba($navy, 0.85);

    &.is-prev { left: 12px; }
    &.is-next { right: 12px; }
  }

  .services__caption-overlay {
    display: block;
  }

  .services__caption {
    display: none;
  }
}
</style>
